import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { useSidebar } from '../../context/SidebarContext';
import { useAuth } from '../../context/AuthContext';
import SearchBar from '../common/SearchBar';
import { LuSun, LuMoon, LuMenu, LuUser, ChevronDown, Settings, LogOut } from '../../utils/icons';
import { getPageMeta } from '../../pages/pageMeta';

const Header: React.FC = () => {
  const { theme, toggleTheme } = useTheme();
  const { toggleSidebar } = useSidebar();
  const { logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const meta = getPageMeta(location.pathname);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const profileDropdownRef = useRef<HTMLLIElement>(null);
  const SunIcon = LuSun as React.ComponentType<{ size?: number; className?: string }>;
  const MoonIcon = LuMoon as React.ComponentType<{ size?: number; className?: string }>;
  const MenuIcon = LuMenu as React.ComponentType<{ size?: number; className?: string }>;
  const UserIcon = LuUser as React.ComponentType<{ size?: number; className?: string }>;
  const ChevronDownIcon = ChevronDown as React.ComponentType<{ size?: number; className?: string }>;
  const SettingsIcon = Settings as React.ComponentType<{ size?: number; className?: string }>;
  const LogOutIcon = LogOut as React.ComponentType<{ size?: number; className?: string }>;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target as Node)) {
        setIsProfileOpen(false);
      }
    };

    if (isProfileOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isProfileOpen]);

  const handleLogout = async () => {
    setIsProfileOpen(false);
    await logout();
    navigate('/login');
  };

  return (
    <nav className={`sticky top-0 xl:top-4 z-50 flex flex-wrap items-center justify-between px-6 py-2 mx-0 transition-all shadow-none duration-250 ease-soft-in rounded-2xl lg:flex-nowrap lg:justify-start ${
      isScrolled ? 'bg-white/70 dark:bg-slate-800/70 backdrop-blur supports-[backdrop-filter]:bg-white/60 dark:supports-[backdrop-filter]:bg-slate-800/60' : 'bg-transparent'
    }`}>
      <div className="flex items-center justify-between w-full px-0 py-1 mx-auto flex-wrap-inherit">
        <div className="flex items-start gap-3">
          <div>
            <h1 className="mb-0 font-bold text-lg text-slate-700 dark:text-slate-200 capitalize">{meta.title}</h1>
            <ol className="mt-1 flex flex-wrap bg-transparent rounded-lg">
              <li className="leading-normal text-[11px]">
                <Link className="opacity-50 text-slate-600 dark:text-slate-400" to="/dashboard">Pages</Link>
              </li>
              <li
                className="text-[11px] pl-2 capitalize leading-normal text-slate-600 dark:text-slate-400 before:float-left before:pr-2 before:text-gray-500 dark:before:text-slate-500 before:content-['/']"
                aria-current="page"
              >
                {meta.section}
              </li>
            </ol>
          </div>
        </div>

        <div className="flex items-center mt-2 grow sm:mt-0 sm:mr-6 md:mr-0 lg:flex lg:basis-auto">
          <div className="flex items-center md:ml-auto md:pr-4">
            <SearchBar placeholder="Type here..." />
          </div>
          <ul className="flex flex-row justify-end pl-0 mb-0 list-none md-max:w-full">
            <li className="flex items-center">
              <button
                onClick={toggleTheme}
                className="block px-0 py-2 font-semibold transition-all ease-nav-brand text-sm text-slate-500 dark:text-slate-400"
                title="Toggle theme"
                aria-label="Toggle theme"
              >
                {theme === 'dark' ? (
                  <SunIcon className="inline" size={16} />
                ) : (
                  <MoonIcon className="inline" size={16} />
                )}
              </button>
            </li>
            <li className="flex items-center pl-4 xl:hidden">
              <button
                onClick={toggleSidebar}
                className="block p-0 transition-all ease-nav-brand text-sm text-slate-500 dark:text-slate-400"
                aria-label="Toggle sidebar"
              >
                <MenuIcon size={20} />
              </button>
            </li>
            <li ref={profileDropdownRef} className="relative flex items-center pl-4">
              <button
                onClick={() => setIsProfileOpen(!isProfileOpen)}
                className="flex items-center gap-1.5 px-0 py-2 font-semibold transition-all ease-nav-brand text-sm text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
                aria-label="Profile menu"
                aria-expanded={isProfileOpen}
              >
                <UserIcon className="inline" size={16} />
                <ChevronDownIcon 
                  className={`inline transition-transform duration-200 ${isProfileOpen ? 'rotate-180' : ''}`} 
                  size={14} 
                />
              </button>

              {isProfileOpen && (
                <div className="absolute right-0 top-full mt-2 w-48 rounded-lg bg-white dark:bg-slate-800 shadow-lg border border-gray-100 dark:border-slate-600 py-1 z-50">
                  <Link
                    to="/dashboard/profile"
                    onClick={() => setIsProfileOpen(false)}
                    className="flex items-center gap-2 px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
                  >
                    <UserIcon size={16} />
                    <span>Profile</span>
                  </Link>
                  <Link
                    to="/dashboard/settings"
                    onClick={() => setIsProfileOpen(false)}
                    className="flex items-center gap-2 px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
                  >
                    <SettingsIcon size={16} />
                    <span>Settings</span>
                  </Link>
                  <hr className="my-1 border-gray-100 dark:border-slate-600" />
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors text-left"
                  >
                    <LogOutIcon size={16} />
                    <span>Logout</span>
                  </button>
                </div>
              )}
            </li>
          </ul>
        </div>
      </div>
    </nav>
  );
};

export default Header;
